import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const GdprPage = () => {
  return (
    <div className="min-h-screen bg-[var(--vp-paper)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--vp-line)] sticky top-0 z-50">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[var(--vp-ink)] hover:text-[var(--vp-pink)] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Zpět na hlavní stránku</span>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[var(--vp-pink)]" />
              <span className="font-semibold text-[var(--vp-ink)]">GDPR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container-custom py-12 max-w-4xl"
      >
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[var(--shadow-soft)] border border-[var(--vp-line)]">
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--vp-ink)] mb-2">
            Ochrana osobních údajů (GDPR)
          </h1>
          <p className="text-[var(--vp-ink)]/60 mb-8">
            služby „Výzva Přijata"
          </p>

          <div className="prose prose-lg max-w-none text-[var(--vp-ink)]/80">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">1. Správce osobních údajů</h2>
              <p className="leading-relaxed">
                Správcem osobních údajů je:<br /><br />
                Jakub Poloch<br />
                IČO: 23393891<br />
                Sídlo: Fráni Šrámka 2457/28, 709 00 Ostrava, Česká republika<br />
                E-mail: <a href="mailto:info@vyzvaprijata.eu" className="text-[var(--vp-pink)] hover:underline">info@vyzvaprijata.eu</a><br />
                Telefon: +420 721 665 622
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">2. Rozsah zpracovávaných osobních údajů</h2>
              <p className="mb-4">Pro účely poskytování služby jsou zpracovávány následující osobní údaje:</p>
              <ul className="list-disc list-inside mb-4 space-y-1">
                <li>e-mailová adresa</li>
                <li>telefonní číslo</li>
                <li>technické identifikátory související s využíváním služby</li>
                <li>údaje nezbytné pro zpracování platby</li>
              </ul>
              <p>Nejsou zpracovávány citlivé osobní údaje ani obsah komunikace mezi partnery.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">3. Účel zpracování osobních údajů</h2>
              <ul className="list-disc list-inside space-y-1">
                <li>poskytování digitální služby</li>
                <li>plnění smlouvy</li>
                <li>vyřízení reklamací</li>
                <li>plnění zákonných povinností</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">4. Právní základ zpracování</h2>
              <p className="mb-4">Osobní údaje jsou zpracovávány na základě:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>čl. 6 odst. 1 písm. b) GDPR – plnění smlouvy</li>
                <li>čl. 6 odst. 1 písm. c) GDPR – splnění právní povinnosti</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">5. Doba uchování osobních údajů</h2>
              <p className="mb-4">Osobní údaje jsou uchovávány po dobu trvání smluvního vztahu a následně po dobu stanovenou příslušnými právními předpisy, zejména daňovými a účetními předpisy (až 10 let).</p>
              <p>Údaje, které nejsou vyžadovány právními předpisy, jsou uchovávány pouze po nezbytně nutnou dobu pro ochranu práv a oprávněných zájmů Poskytovatele.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">6. Příjemci osobních údajů</h2>
              <p className="mb-4">Osobní údaje mohou být předány následujícím zpracovatelům:</p>
              <ul className="list-disc list-inside mb-4 space-y-1">
                <li>poskytovatel hostingu</li>
                <li>provozovatel SMS brány</li>
                <li>platební brána Stripe</li>
                <li>poskytovatel účetních služeb</li>
              </ul>
              <p>Osobní údaje mohou být zpracovávány i mimo území Evropské unie, pokud je to nezbytné pro poskytování služby (např. prostřednictvím poskytovatelů technických nebo platebních služeb). V takovém případě jsou přijata odpovídající záruky v souladu s GDPR.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">7. Práva subjektu údajů</h2>
              <p className="mb-4">Uživatel má právo:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>na přístup ke svým osobním údajům</li>
                <li>na opravu nepřesných údajů</li>
                <li>na výmaz osobních údajů</li>
                <li>na omezení zpracování</li>
                <li>na přenositelnost údajů</li>
                <li>vznést námitku proti zpracování</li>
                <li>podat stížnost u dozorového úřadu, kterým je Úřad pro ochranu osobních údajů, Pplk. Sochora 27, 170 00 Praha 7, <a href="https://www.uoou.cz" target="_blank" rel="noopener noreferrer" className="text-[var(--vp-pink)] hover:underline">www.uoou.cz</a></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">8. Zabezpečení osobních údajů</h2>
              <p>Správce přijal odpovídající technická a organizační opatření k zabezpečení osobních údajů proti neoprávněnému přístupu, ztrátě nebo zneužití.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-[var(--vp-ink)] mb-4">9. Závěrečná ustanovení</h2>
              <p>Tyto zásady ochrany osobních údajů jsou účinné dnem zveřejnění.</p>
            </section>
          </div>
        </div>

        {/* Back button */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button className="btn-primary px-6 py-3 rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět na hlavní stránku
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default GdprPage;
